import { useEffect, useRef, useState } from 'react'

import { Button } from '@mantine/core'

import { useApolloClient } from '@apollo/client'
import { navigate, useParams } from '@redwoodjs/router'
import { useMutation } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  SYNC_APPLICATION_QUESTION_RESPONSES,
  UPDATE_APPLICATION_QUESTION_RESPONSE,
} from 'src/graphql/customQuestions.graphql'
import { CustomQuestionFieldType } from 'src/graphql/types/customQuestions'

import ApplicationMultiSelectInput from '../components/ApplicationMultiSelectInput'
import ApplicationRadioQuestion from '../components/ApplicationRadioQuestion'
import ApplicationScreenWrapper from '../components/ApplicationScreenWrapper'
import ApplicationSelectInput from '../components/ApplicationSelectInput'
import ApplicationTextareaInput from '../components/ApplicationTextareaInput'
import ApplicationTextInput from '../components/ApplicationTextInput'
import { useLeaveGuard } from '../hooks/useLeaveGuard'
import { useFormContext } from '../utils/FormContext'
import { filterPrivateQuestionResponses } from '../utils/questionResponses'
import {
  questionValuesFromResponses,
  validateQuestionValues,
} from '../utils/questionValidation'

const syncedApplicationIds = new Set<string>()

export const CompanyQuestionsScreen = () => {
  const {
    goBack,
    goToReview,
    fromReview,
    backText,
    handleNext,
    updateLoading,
    application,
  } = useFormContext()
  const { applicationId } = useParams()
  const client = useApolloClient()

  // Initialize state for question responses
  const [questionValues, setQuestionValues] = useState({})
  const [errors, setErrors] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [hasUnsavedAnswers, setHasUnsavedAnswers] = useState(false)
  const leaveGuard = useLeaveGuard({
    isDirty: hasUnsavedAnswers,
    save: () => saveQuestions(),
    discard: () => discardAnswers(),
    saving: isSaving,
  })
  // True once the sync round-trip has actually finished (or was already done
  // by a previous mount). The consents redirect must wait for this — firing on
  // sync *initiation* can skip PRIVATE questions the sync is about to create.
  const [syncSettled, setSyncSettled] = useState(false)
  // Sync failed: show a retry UI instead of a blank render — every applicant
  // routes through this screen, so a silent null strands the whole flow.
  const [syncError, setSyncError] = useState(false)

  // Track if we've already synced to prevent infinite loops
  const hasSyncedRef = useRef(false)
  const currentApplicationRef = useRef(null)
  const touchedQuestionIdsRef = useRef(new Set())

  // PUBLIC questions belong to the public short form only — the DOT
  // application displays, validates, and saves PRIVATE questions exclusively.
  // Their response rows may still arrive (pre-answered from the short form);
  // they are filtered out before entering this screen's state.
  const privateQuestionResponses = filterPrivateQuestionResponses(
    application?.questionResponses
  )

  const [syncQuestionResponses, { loading: syncLoading }] = useMutation(
    SYNC_APPLICATION_QUESTION_RESPONSES,
    {
      onCompleted: () => {
        // Mark the app as synced across mounts only now — adding the key when
        // the sync *starts* would let a remount during an in-flight sync treat
        // the app as already synced and redirect before rows exist.
        const syncApplicationKey =
          application?.applicationId || applicationId || null
        if (syncApplicationKey) {
          syncedApplicationIds.add(syncApplicationKey)
        }
        setSyncError(false)
        setSyncSettled(true)
      },
      onError: (error) => {
        console.error('Unable to sync responses:', error)
        // Allow retries and avoid redirecting past unanswered questions when sync fails.
        hasSyncedRef.current = false
        setSyncSettled(false)
        setSyncError(true)
        const syncApplicationKey =
          application?.applicationId || applicationId || null
        if (syncApplicationKey) {
          syncedApplicationIds.delete(syncApplicationKey)
        }
      },
      refetchQueries: ['Application'],
      awaitRefetchQueries: true,
    }
  )

  const [updateQuestionResponse] = useMutation(
    UPDATE_APPLICATION_QUESTION_RESPONSE,
    {
      onError: (error) => {
        toast(error.message || 'Unable to save response', 'error')
      },
    }
  )

  // Sync question responses once per mount to pick up any newly added template questions.
  // The sync mutation is idempotent — it only creates rows for questions that don't have one yet.
  useEffect(() => {
    // Reset sync flag if application changed
    if (currentApplicationRef.current !== applicationId) {
      hasSyncedRef.current = false
      currentApplicationRef.current = applicationId
      touchedQuestionIdsRef.current.clear()
      setSyncSettled(false)
    }

    const applicantId = application?.applicant?.applicantId
    const jobListingId = application?.applicant?.jobListing?.jobListingId
    const syncApplicationKey =
      application?.applicationId || applicationId || null
    const alreadySyncedAcrossMounts = syncApplicationKey
      ? syncedApplicationIds.has(syncApplicationKey)
      : false

    if (
      applicantId &&
      jobListingId &&
      application?.applicationId &&
      !hasSyncedRef.current
    ) {
      hasSyncedRef.current = true
      if (alreadySyncedAcrossMounts) {
        // A previous mount already COMPLETED the sync (the key is only added
        // in onCompleted) — settle immediately so a remount (e.g. browser
        // Back) can still evaluate the redirect instead of stranding
        // PUBLIC-only applications on a blank render.
        setSyncSettled(true)
      } else {
        // Not yet completed anywhere (including a remount that interrupted an
        // in-flight sync): fire the idempotent sync; onCompleted settles us.
        syncQuestionResponses({
          variables: {
            applicationId: application.applicationId,
            applicantId,
            jobListingId,
          },
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    application?.applicationId,
    application?.applicant?.applicantId,
    application?.applicant?.jobListing?.jobListingId,
    applicationId,
    // syncQuestionResponses omitted — stable from useMutation
    // syncLoading omitted — hasSyncedRef is the guard; including syncLoading
    // would re-fire the effect on error and create an infinite retry loop
  ])

  // Initialize values from application data.
  // Depends on questionResponses length (a primitive) rather than the application object,
  // because Apollo returns a new application reference after refetch — using [application]
  // would cause an infinite re-render loop since setQuestionValues always creates new state.
  useEffect(() => {
    if (application?.questionResponses) {
      const incomingValues = questionValuesFromResponses(
        privateQuestionResponses
      )
      // Preserve in-progress edits while allowing new synced questions to appear.
      // Returns prev when nothing changed so React can bail out (no re-render).
      setQuestionValues((prev) => {
        let changed = false
        const next = { ...prev }

        Object.entries(incomingValues).forEach(
          ([questionId, incomingValue]) => {
            if (!touchedQuestionIdsRef.current.has(questionId)) {
              if (prev[questionId] !== incomingValue) {
                changed = true
              }
              next[questionId] = incomingValue
            }
          }
        )

        // Detect new or removed keys
        if (Object.keys(next).length !== Object.keys(prev).length) {
          changed = true
        }

        return changed ? next : prev
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privateQuestionResponses.length])

  // Check if application has custom questions, if not redirect to consents
  // Don't redirect while syncing - wait for sync to complete first
  // An application whose questions are all PUBLIC (short form) behaves
  // exactly like one with no questions at all.
  useEffect(() => {
    if (
      application?.questionResponses &&
      privateQuestionResponses.length === 0 &&
      !syncLoading &&
      syncSettled
    ) {
      // replace: this screen has nothing to show, so it must not stay in
      // history — otherwise browser Back from consents bounces straight
      // back to consents and the driver can never reach earlier steps.
      if (fromReview) {
        goToReview({ replace: true })
      } else {
        navigate(
          `/driver-application/consents?applicationId=${applicationId}`,
          { replace: true }
        )
      }
    }
    // syncSettled (state) gates the redirect on sync *completion* — firing on
    // initiation could skip PRIVATE questions the sync is about to create.
    // application?.applicationId is a dep so the redirect re-evaluates when
    // the query data arrives after mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    privateQuestionResponses.length,
    applicationId,
    application?.applicationId,
    syncLoading,
    syncSettled,
  ])

  const retrySync = () => {
    const applicantId = application?.applicant?.applicantId
    const jobListingId = application?.applicant?.jobListing?.jobListingId
    if (!applicantId || !jobListingId || !application?.applicationId) {
      return
    }
    setSyncError(false)
    hasSyncedRef.current = true
    syncQuestionResponses({
      variables: {
        applicationId: application.applicationId,
        applicantId,
        jobListingId,
      },
    })
  }

  // Sync failed and we have nothing to show: offer a retry instead of a blank
  // page — the redirect is gated on a successful sync and will never fire here.
  if (
    syncError &&
    application?.questionResponses &&
    privateQuestionResponses.length === 0 &&
    !syncLoading
  ) {
    return (
      <ApplicationScreenWrapper
        title={'Company Questions'}
        description="Please answer the following questions from the company."
        goBack={goBack}
        updateLoading={false}
        application={application}
        handleCancel={goBack}
        handleSubmit={retrySync}
      >
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-doubleNickel-gray-700">
            We couldn&apos;t load the company questions. Check your connection
            and try again.
          </p>
          <Button variant="outline" size="sm" onClick={retrySync}>
            Try again
          </Button>
        </div>
      </ApplicationScreenWrapper>
    )
  }

  // Don't render if no questions, but show loading state while syncing
  if (
    !application ||
    (application.questionResponses &&
      privateQuestionResponses.length === 0 &&
      !syncLoading)
  ) {
    return null
  }

  const handleValueChange = (questionResponseId, value) => {
    touchedQuestionIdsRef.current.add(questionResponseId)
    setHasUnsavedAnswers(true)
    setQuestionValues((prev) => ({
      ...prev,
      [questionResponseId]: value,
    }))
  }

  const sortedQuestionResponses = [...privateQuestionResponses].sort(
    (a, b) => a.templateQuestion.order - b.templateQuestion.order
  )

  const validateQuestions = () =>
    validateQuestionValues(sortedQuestionResponses, questionValues)

  const saveQuestions = async (): Promise<boolean> => {
    const validationErrors = validateQuestions()

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      // Scroll to first error
      const firstErrorElement = document.querySelector('[data-error="true"]')
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
      return false
    }

    // Clear errors
    setErrors({})
    setIsSaving(true)

    try {
      // Save all question responses
      const savePromises = sortedQuestionResponses.map((response) => {
        const questionResponseId = response.applicationQuestionResponseId
        const value = questionValues[questionResponseId]
        const fieldType = response.templateQuestion.question.fieldType

        // Build input based on field type
        const input: {
          valueShortText?: string
          valueLongText?: string
          valueBoolean?: boolean
          selectedOptionIds?: string[]
        } = {}

        if (
          fieldType === CustomQuestionFieldType.SHORT_TEXT ||
          fieldType === CustomQuestionFieldType.LONG_TEXT
        ) {
          if (fieldType === CustomQuestionFieldType.SHORT_TEXT) {
            input.valueShortText = value || null
          } else {
            input.valueLongText = value || null
          }
        } else if (fieldType === CustomQuestionFieldType.BOOLEAN) {
          input.valueBoolean = value === 'true'
        } else if (fieldType === CustomQuestionFieldType.SINGLE_SELECT) {
          input.selectedOptionIds = value ? [value] : []
        } else if (fieldType === CustomQuestionFieldType.MULTI_SELECT) {
          input.selectedOptionIds = value || []
        }

        return updateQuestionResponse({
          variables: {
            id: questionResponseId,
            input,
          },
        })
      })

      await Promise.all(savePromises)
      await client.refetchQueries({ include: ['Application'] })
      setHasUnsavedAnswers(false)
      toast('Responses saved successfully', 'success')
      return true
    } catch (error) {
      console.error('Error saving responses:', error)
      toast('Failed to save some responses', 'error')
      return false
    } finally {
      setIsSaving(false)
    }
  }

  const handleSubmitQuestions = async () => {
    if (await saveQuestions()) handleNext({})
  }

  const discardAnswers = () => {
    setQuestionValues(questionValuesFromResponses(privateQuestionResponses))
    setErrors({})
    touchedQuestionIdsRef.current.clear()
    setHasUnsavedAnswers(false)
  }

  const renderQuestionInput = (response) => {
    const { fieldType, label, placeholder, options } =
      response.templateQuestion.question
    const questionResponseId = response.applicationQuestionResponseId
    const value = questionValues[questionResponseId]
    const isRequired = response.templateQuestion.isRequired
    const error = errors[questionResponseId]

    // Format options for Mantine components
    const formattedOptions = options
      ? [...options]
          .sort((a, b) => a.order - b.order)
          .map((option) => ({
            value: option.customQuestionOptionId,
            label: option.label,
          }))
      : []

    switch (fieldType) {
      case CustomQuestionFieldType.SHORT_TEXT:
        return (
          <ApplicationTextInput
            label={label}
            placeholder={placeholder || 'Enter your answer'}
            value={value || ''}
            onChange={(e) =>
              handleValueChange(questionResponseId, e.target.value)
            }
            required={isRequired}
            error={error}
          />
        )

      case CustomQuestionFieldType.LONG_TEXT:
        return (
          <ApplicationTextareaInput
            label={label}
            placeholder={placeholder || 'Enter your answer'}
            value={value || ''}
            onChange={(e) =>
              handleValueChange(questionResponseId, e.target.value)
            }
            required={isRequired}
            error={error}
          />
        )

      case CustomQuestionFieldType.BOOLEAN:
        return (
          <ApplicationRadioQuestion
            question={label}
            value={value}
            handleChange={(val) => handleValueChange(questionResponseId, val)}
            required={isRequired}
            error={error}
          />
        )

      case CustomQuestionFieldType.SINGLE_SELECT:
        return (
          <ApplicationSelectInput
            label={label}
            placeholder={placeholder || 'Select an option'}
            value={value || ''}
            onChange={(e) =>
              handleValueChange(questionResponseId, e.target.value)
            }
            data={[
              {
                value: '',
                label: placeholder || 'Select an option',
                disabled: true,
              },
              ...formattedOptions,
            ]}
            required={isRequired}
            error={error}
          />
        )

      case CustomQuestionFieldType.MULTI_SELECT:
        return (
          <ApplicationMultiSelectInput
            label={label}
            placeholder={placeholder || 'Select options'}
            value={value || []}
            onChange={(val) => handleValueChange(questionResponseId, val)}
            data={formattedOptions}
            required={isRequired}
            error={error}
          />
        )

      default:
        return (
          <p className="text-sm text-gray-500">
            Unsupported field type: {fieldType}
          </p>
        )
    }
  }

  return (
    <ApplicationScreenWrapper
      title={'Company Questions'}
      description="Please answer the following questions from the company."
      goBack={leaveGuard.goBack}
      updateLoading={updateLoading || isSaving || syncLoading}
      application={application}
      handleCancel={leaveGuard.goBack}
      backText={backText}
      saveButtontext={'Save and continue'}
      handleSubmit={handleSubmitQuestions}
    >
      {leaveGuard.leaveGuardModal}
      <div className="flex flex-col gap-6">
        {sortedQuestionResponses.map((response) => (
          <div
            key={response.applicationQuestionResponseId}
            data-error={!!errors[response.applicationQuestionResponseId]}
          >
            {response.templateQuestion.question.helpText && (
              <p className="mb-2 text-sm text-gray-600">
                {response.templateQuestion.question.helpText}
              </p>
            )}
            {renderQuestionInput(response)}
          </div>
        ))}
      </div>
    </ApplicationScreenWrapper>
  )
}

export default CompanyQuestionsScreen
